import React, { useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { SEOHead } from '../../components/seo/SEOHead';
import { LandingNav } from './LandingNav';
import { LandingFooter } from './LandingFooter';
import { useLandingTheme } from './useLandingTheme';
import { useLandingMotion } from './useLandingMotion';
import { FONT_MONO, FONT_SANS, FONT_SERIF, SUPPORT_EMAIL } from './landingData';
import { FAQ_GROUPS } from './faqData';
import { useLandingLang, type LandingLang } from './useLandingLang';
import './landing.css';

const tw = (v: string) => ({ textWrap: v } as React.CSSProperties);
const eyebrow: React.CSSProperties = { fontFamily: FONT_MONO, fontSize: 10, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--hv-leaf)' };

const COPY = {
    en: {
        title: <>Questions, <i>answered</i></>,
        lead: 'Everything about building, publishing and paying for your site on HyVay. Can\'t find yours? Our team replies by email.',
        more: 'Still have a question?',
        moreBody: 'Tell us what you are trying to do and we will get back to you.',
        start: 'Create a free account',
        build: 'Start building',
        seoDescription: 'Answers about HyVay: how the AI website builder works, credits and payment, publishing, custom domains, built-in analytics, and your data.',
    },
    id: {
        title: <>Pertanyaan, <i>terjawab</i></>,
        lead: 'Semua tentang membuat, menayangkan, dan membayar situsmu di HyVay. Tidak menemukan pertanyaanmu? Tim kami membalas lewat email.',
        more: 'Masih ada pertanyaan?',
        moreBody: 'Ceritakan apa yang ingin kamu lakukan, dan kami akan membalas.',
        start: 'Buat akun gratis',
        build: 'Mulai membuat',
        seoDescription: 'Jawaban seputar HyVay: cara kerja AI website builder, credit dan pembayaran, publish, custom domain, analytics bawaan, dan data kamu.',
    },
} as const;

export const FaqPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, isAuthenticated, logout } = useAuth();
    const [theme, toggleTheme] = useLandingTheme();
    const rootRef = useRef<HTMLDivElement>(null);
    useLandingMotion(rootRef);
    const [lang, setLang] = useLandingLang();
    const t = COPY[lang];

    // /faq#refund opens and scrolls to that question (or a whole group).
    const hash = location.hash.slice(1);
    useEffect(() => {
        if (!hash) return;
        const el = document.getElementById(hash);
        if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
    }, [hash]);

    const structuredData = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        inLanguage: lang,
        mainEntity: FAQ_GROUPS.flatMap((g) => g.items).map((it) => ({
            '@type': 'Question',
            name: it.q[lang],
            acceptedAnswer: { '@type': 'Answer', text: it.a[lang] },
        })),
    };

    return (
        <div ref={rootRef} className="hv-root" data-hv-theme={theme} lang={lang} style={{ position: 'static', fontFamily: FONT_SANS, color: 'var(--hv-ink)', background: 'var(--hv-desk)', overflowX: 'clip', minHeight: '100vh', WebkitFontSmoothing: 'antialiased' }}>
            <SEOHead
                title="FAQ"
                description={t.seoDescription}
                url="https://hyvay.com/faq"
                canonicalUrl="https://hyvay.com/faq"
                keywords={['hyvay faq', 'ai website builder', 'buat website umkm', 'harga hyvay', 'custom domain']}
                structuredData={structuredData}
            />

            <section data-screen-label="FAQ hero" style={{ position: 'relative', overflow: 'hidden', background: 'var(--hv-page)' }}>
                <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: 0.7, backgroundImage: 'radial-gradient(circle,var(--hv-line-2) 1.3px,transparent 1.9px)', backgroundSize: '24px 24px', maskImage: 'radial-gradient(ellipse 70% 50% at 50% 0%,black,transparent)', WebkitMaskImage: 'radial-gradient(ellipse 70% 50% at 50% 0%,black,transparent)' }} />
                <LandingNav theme={theme} onToggleTheme={toggleTheme} isAuthenticated={isAuthenticated} avatarUrl={user?.avatar} userName={user?.fullName} onLogout={logout} />

                <div className="hv-shell hv-hero-pad" style={{ position: 'relative', zIndex: 1, maxWidth: 1280, margin: '0 auto', padding: '64px 28px 56px' }}>
                    <div style={{ maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>
                        <div style={eyebrow}>FAQ</div>
                        <h1 className="hv-h1" style={{ margin: '16px 0 0', fontFamily: FONT_SERIF, fontWeight: 400, fontSize: 72, lineHeight: 1.0, letterSpacing: '-0.025em', color: 'var(--hv-ink)', ...tw('balance') }}>{t.title}</h1>
                        <p style={{ margin: '22px auto 0', maxWidth: 560, fontSize: 16.5, lineHeight: 1.6, color: 'var(--hv-body)', ...tw('pretty') }}>{t.lead}</p>

                        <div role="radiogroup" aria-label="Language" style={{ display: 'inline-flex', gap: 3, marginTop: 26, padding: 3, borderRadius: 999, background: 'var(--hv-page-2)', border: '1px solid var(--hv-line)' }}>
                            {([['en', 'English'], ['id', 'Bahasa Indonesia']] as [LandingLang, string][]).map(([code, label]) => (
                                <button
                                    key={code}
                                    type="button"
                                    role="radio"
                                    aria-checked={lang === code}
                                    data-track="faq_language"
                                    data-track-label={code}
                                    onClick={() => setLang(code)}
                                    style={{ height: 34, padding: '0 16px', borderRadius: 999, border: 0, cursor: 'pointer', fontFamily: FONT_SANS, fontSize: 13, fontWeight: 700, background: lang === code ? 'var(--hv-card)' : 'transparent', color: lang === code ? 'var(--hv-ink)' : 'var(--hv-muted)', boxShadow: lang === code ? '0 1px 2px var(--hv-shadow)' : undefined }}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Jump links to each group */}
                    <nav aria-label="FAQ topics" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 8, marginTop: 36 }}>
                        {FAQ_GROUPS.map((g) => (
                            <a key={g.id} href={`#${g.id}`} className="hv-h-brand-bd" style={{ display: 'inline-flex', alignItems: 'center', height: 36, padding: '0 15px', borderRadius: 999, border: '1px solid var(--hv-line)', background: 'var(--hv-card)', fontSize: 13, fontWeight: 600, color: 'var(--hv-ink)' }}>
                                {g.title[lang]}
                            </a>
                        ))}
                    </nav>
                </div>
            </section>

            <section data-screen-label="FAQ list" className="hv-band" style={{ padding: '56px 0 88px', background: 'var(--hv-page)', borderTop: '1px solid var(--hv-line)' }}>
                <div className="hv-shell" style={{ maxWidth: 880, margin: '0 auto', padding: '0 28px', display: 'flex', flexDirection: 'column', gap: 48 }}>
                    {FAQ_GROUPS.map((g) => (
                        <div key={g.id} id={g.id} style={{ scrollMarginTop: 24 }}>
                            <h2 className="hv-h2" style={{ margin: 0, fontFamily: FONT_SERIF, fontWeight: 400, fontSize: 40, lineHeight: 1.05, letterSpacing: '-0.02em', color: 'var(--hv-ink)' }}>{g.title[lang]}</h2>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 20 }}>
                                {g.items.map((it) => (
                                    <details key={it.id} id={it.id} className="hv-faq" open={hash === it.id} style={{ borderRadius: 18, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', scrollMarginTop: 24 }}>
                                        <summary data-track="faq_question" data-track-label={it.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '18px 20px', cursor: 'pointer', listStyle: 'none', fontSize: 15.5, fontWeight: 700, lineHeight: 1.4, color: 'var(--hv-ink)' }}>
                                            <span style={{ flex: 1, ...tw('pretty') }}>{it.q[lang]}</span>
                                            <span className="hv-faq-icon" aria-hidden="true" style={{ flex: 'none', width: 28, height: 28, borderRadius: 999, background: 'var(--hv-mint)', color: 'var(--hv-mint-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'transform 0.2s ease' }}>
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
                                            </span>
                                        </summary>
                                        <p style={{ margin: 0, padding: '0 20px 20px', fontSize: 14.5, lineHeight: 1.7, color: 'var(--hv-body)', ...tw('pretty') }}>{it.a[lang]}</p>
                                    </details>
                                ))}
                            </div>
                        </div>
                    ))}

                    {/* Contact */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap', padding: 28, borderRadius: 24, background: 'var(--hv-mint)' }}>
                        <div style={{ flex: 1, minWidth: 240 }}>
                            <h2 style={{ margin: 0, fontFamily: FONT_SERIF, fontWeight: 400, fontSize: 30, letterSpacing: '-0.015em', color: 'var(--hv-mint-ink)' }}>{t.more}</h2>
                            <p style={{ margin: '6px 0 0', fontSize: 14, lineHeight: 1.6, color: 'var(--hv-mint-ink)' }}>
                                {t.moreBody} <a href={`mailto:${SUPPORT_EMAIL}`} data-track="faq_email" style={{ fontWeight: 700, color: 'var(--hv-mint-ink)', textDecoration: 'underline' }}>{SUPPORT_EMAIL}</a>
                            </p>
                        </div>
                        <button
                            type="button"
                            data-track="faq_start"
                            onClick={() => navigate(isAuthenticated ? '/' : '/signup')}
                            className="hv-h-deep"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 46, padding: '0 20px', borderRadius: 999, border: 0, background: 'var(--brand,#13422C)', color: '#FFFDF7', fontFamily: FONT_SANS, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
                        >
                            {isAuthenticated ? t.build : t.start}
                        </button>
                    </div>
                    <p style={{ margin: '-28px 0 0', fontSize: 12.5, color: 'var(--hv-muted)' }}>
                        <Link to="/pricing" style={{ color: 'var(--hv-muted)', textDecoration: 'underline' }}>Pricing</Link> · <Link to="/terms" style={{ color: 'var(--hv-muted)', textDecoration: 'underline' }}>Terms</Link> · <Link to="/privacy" style={{ color: 'var(--hv-muted)', textDecoration: 'underline' }}>Privacy</Link>
                    </p>
                </div>
            </section>

            <LandingFooter />
        </div>
    );
};

export default FaqPage;
